import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Copy, Download, FileText, Folder } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { RuntimeConfig } from '@/lib/runtime-config';
import { formatBytes, formatDateTime, formatRelativeTime } from '@/lib/format';
import { strings } from '@/lib/strings';
import {
  getDownloadUrl,
  getRepoDevices,
  getRepoEntries,
  getRepoStatus,
  type RepoDevice,
  type RepoEntriesResponse,
  type RepoStatus,
  WebApiError
} from '@/lib/web-api';

interface RepoBrowserPageProps {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
}

const buildRepoRoute = (repoId: string, segments: string[]) => {
  if (!segments.length) {
    return `/repos/${repoId}`;
  }
  const encoded = segments.map(segment => encodeURIComponent(segment)).join('/');
  return `/repos/${repoId}/files/${encoded}`;
};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const mapStatusLabel = (status?: RepoStatus) => {
  const state = status?.state?.toUpperCase();
  switch (state) {
    case 'IDLE':
      return { label: strings.syncState.idle, variant: 'success' as const };
    case 'SYNCING':
      return { label: strings.syncState.syncing, variant: 'accent' as const };
    case 'ERROR':
      return { label: strings.syncState.error, variant: 'error' as const };
    case 'OFFLINE':
      return { label: strings.syncState.offline, variant: 'warning' as const };
    default:
      return { label: strings.syncState.unknown, variant: 'default' as const };
  }
};

export const RepoBrowserPage = ({ runtimeConfig, tenantId }: RepoBrowserPageProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const repoId = params.repoId;
  const splat = params['*'];

  const segments = useMemo(() => {
    if (!splat) {
      return [] as string[];
    }
    return splat.split('/').filter(Boolean).map(decodeSegment);
  }, [splat]);

  const currentPath = segments.join('/');

  const [entries, setEntries] = useState<RepoEntriesResponse | null>(null);
  const [status, setStatus] = useState<RepoStatus | null>(null);
  const [devices, setDevices] = useState<RepoDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!repoId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      setEntries(null);
      setStatus(null);
      setDevices([]);

      try {
        const [entriesResponse, statusResponse, devicesResponse] = await Promise.all([
          getRepoEntries(runtimeConfig, tenantId, repoId, currentPath || undefined),
          getRepoStatus(runtimeConfig, tenantId, repoId),
          getRepoDevices(runtimeConfig, tenantId, repoId)
        ]);

        if (cancelled) {
          return;
        }

        setEntries(entriesResponse);
        setStatus(statusResponse);
        setDevices(devicesResponse);
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (err instanceof WebApiError) {
          setError(err.code === 'IndexNotReady'
            ? 'Snapshot index is still building. Try again in a moment.'
            : err.message);
        } else {
          setError('Unable to load repository entries.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [runtimeConfig, tenantId, repoId, currentPath]);

  useEffect(() => {
    if (!copyMessage) {
      return;
    }
    const timeout = window.setTimeout(() => setCopyMessage(null), 1500);
    return () => window.clearTimeout(timeout);
  }, [copyMessage]);

  if (!repoId) {
    return (
      <div className="text-sm text-muted-foreground">Repository not found.</div>
    );
  }

  const breadcrumbItems = [
    { label: 'Repositories', to: '/repos' },
    { label: repoId, to: buildRepoRoute(repoId, []) }
  ];
  segments.forEach((segment, index) => {
    breadcrumbItems.push({
      label: segment,
      to: buildRepoRoute(repoId, segments.slice(0, index + 1))
    });
  });

  const statusBadge = mapStatusLabel(status ?? undefined);
  const syncTime = status?.last_sync_unix_ms;

  const handleDownload = async (path: string) => {
    try {
      const url = await getDownloadUrl(runtimeConfig, tenantId, repoId, path);
      window.location.assign(url);
    } catch (err) {
      if (err instanceof WebApiError) {
        setError(err.message);
      } else {
        setError('Unable to download file.');
      }
    }
  };

  const handleCopyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopyMessage('Path copied to clipboard');
    } catch {
      setCopyMessage('Unable to copy path');
    }
  };

  const entriesList = entries?.entries ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xl">{repoId}</span>
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>{strings.labels.lastSync}: {syncTime ? formatRelativeTime(syncTime) : strings.syncState.unknown}</span>
            <span className="hidden sm:inline">Updated {syncTime ? formatDateTime(syncTime) : '—'}</span>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Browsing</p>
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbItems.map((item, index) => (
                    <BreadcrumbItem key={item.to}>
                      {index < breadcrumbItems.length - 1 ? (
                        <>
                          <Link
                            to={item.to}
                            className="transition-colors hover:text-foreground"
                          >
                            {item.label}
                          </Link>
                          <BreadcrumbSeparator />
                        </>
                      ) : (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(buildRepoRoute(repoId, []))}
              >
                Go to root
              </Button>
            </div>
          </div>
          {copyMessage ? (
            <p className="text-sm text-muted-foreground">{copyMessage}</p>
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : entriesList.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              This folder is empty.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Modified</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entriesList.map(entry => {
                  const isDirectory = entry.type === 'dir';
                  const entryName = entry.path.split('/').pop() ?? entry.path;
                  const entrySegments = entry.path.split('/').filter(Boolean);
                  return (
                    <TableRow key={entry.path}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {isDirectory ? (
                            <span className="rounded-full bg-primary/10 p-2 text-primary">
                              <Folder size={16} />
                            </span>
                          ) : (
                            <span className="rounded-full bg-muted p-2 text-muted-foreground">
                              <FileText size={16} />
                            </span>
                          )}
                          {isDirectory ? (
                            <Link
                              to={buildRepoRoute(repoId, entrySegments)}
                              className="font-medium text-primary hover:underline"
                            >
                              {entryName}
                            </Link>
                          ) : (
                            <span className="font-medium">{entryName}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{isDirectory ? 'Folder' : 'File'}</TableCell>
                      <TableCell>{isDirectory ? '—' : formatBytes(entry.size)}</TableCell>
                      <TableCell>{entry.mtime_unix_ms ? formatRelativeTime(entry.mtime_unix_ms) : '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyPath(entry.path)}
                            aria-label="Copy path"
                          >
                            <Copy size={16} />
                          </Button>
                          {!isDirectory ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(entry.path)}
                              aria-label="Download"
                            >
                              <Download size={16} />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Devices</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : devices.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No device telemetry yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map(device => (
                  <TableRow key={device.device_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{device.device_name ?? device.device_id}</p>
                        {device.user_upn ? (
                          <p className="text-xs text-muted-foreground">{device.user_upn}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{device.platform ?? '—'}</TableCell>
                    <TableCell>{device.last_seen_unix_ms ? formatRelativeTime(device.last_seen_unix_ms) : '—'}</TableCell>
                    <TableCell>{device.last_sync_unix_ms ? formatRelativeTime(device.last_sync_unix_ms) : '—'}</TableCell>
                    <TableCell>{device.state ?? strings.syncState.unknown}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
